/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import { SHOW_DEBUG } from '@/config/app';

export const DEBUG_LOG_LEVELS = [
    'debug',
    'error',
    'info',
    'log',
    'trace',
    'warn',
] as const;

export type DebugLogLevel = typeof DEBUG_LOG_LEVELS[number];

export type DebugConsole = Pick<Console, DebugLogLevel>;

export type DebugLogger = (
    level: DebugLogLevel,
    content: unknown,
    force?: boolean,
) => void;

function serializeDebugLogValue(
    value: unknown,
    seenValues: WeakSet<object> = new WeakSet(),
): string {
    if (value === null) {
        return 'null';
    }

    switch (typeof value) {
        case 'bigint':
            return `bigint:${value.toString()}`;
        case 'boolean':
            return `boolean:${value}`;
        case 'function':
            return `function:${value.toString()}`;
        case 'number':
            return Number.isNaN(value)
                ? 'number:NaN'
                : `number:${Object.is(value, -0) ? '-0' : value}`;
        case 'string':
            return `string:${value}`;
        case 'symbol':
            return `symbol:${value.toString()}`;
        case 'undefined':
            return 'undefined';
        case 'object':
            if (seenValues.has(value)) {
                return '[Circular]';
            }

            if (value instanceof Date) {
                return `date:${value.toISOString()}`;
            }

            if (value instanceof Error) {
                return `error:${value.name}:${value.message}`;
            }

            seenValues.add(value);

            if (Array.isArray(value)) {
                return `[${value.map((entry) => serializeDebugLogValue(entry, seenValues)).join(',')}]`;
            }

            const record = value as Record<string, unknown>;

            return `{${Object.keys(record).sort().map((key) => (
                `${JSON.stringify(key)}:${serializeDebugLogValue(record[key], seenValues)}`
            )).join(',')}}`;
        default:
            return String(value);
    }
}

function createDebugLogKey(level: DebugLogLevel, content: unknown): string {
    return `${level}:${serializeDebugLogValue(content)}`;
}

/**
 * Creates a debug logger that can be disabled without changing individual call sites.
 */
export function createDebugLogger(
    isEnabled: boolean = SHOW_DEBUG,
    debugConsole: DebugConsole = console,
): DebugLogger {
    const loggedMessages = new Set<string>();

    return (level, content, force = false) => {
        if (!isEnabled) {
            return;
        }

        const messageKey = createDebugLogKey(level, content);

        if (!force && loggedMessages.has(messageKey)) {
            return;
        }

        loggedMessages.add(messageKey);
        debugConsole[level](content);
    };
}

/**
 * Use this logger for optional diagnostic output throughout the application.
 */
export const logDebug = createDebugLogger();
